// Calendar and timezone utilities shared by the server and the client bundle
// (architecture.md §7). This module must stay dependency-free: it uses only
// Intl.DateTimeFormat and integer UTC arithmetic, and tests/unit/shared-purity.test.ts
// fails the build if any module dependency is added to src/shared/.
//
// Vocabulary:
//   LocalDate  'YYYY-MM-DD' calendar date in some user's timezone (no instant attached)
//   second     seconds since local midnight, 0..86399
//   instant    a JS Date (UTC epoch milliseconds)

export type LocalDate = string;
export type IanaZone = string;

export const SECONDS_PER_DAY = 86_400;
const MS_PER_SECOND = 1_000;
const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = SECONDS_PER_DAY * MS_PER_SECOND;
const SECONDS_PER_HOUR = 3_600;
const SECONDS_PER_MINUTE = 60;
// Offsets are probed one day either side of a wall-clock time. Real-world zones never
// have two transitions within 24h, so this brackets the "before" and "after" offsets.
const OFFSET_PROBE_MS = MS_PER_DAY;
const LOCAL_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export interface DaySegment {
  date: LocalDate;
  startSecond: number;
  durationSeconds: number;
}

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatterFor(tz: IanaZone): Intl.DateTimeFormat {
  const cached = formatterCache.get(tz);
  if (cached) return cached;
  const created = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  formatterCache.set(tz, created);
  return created;
}

function wallClockAt(instant: Date, tz: IanaZone): WallClock {
  const values: Record<string, number> = {};
  for (const part of formatterFor(tz).formatToParts(instant)) {
    if (part.type !== 'literal') values[part.type] = Number(part.value);
  }
  // Some ICU builds still render midnight as "24" even with h23; normalise defensively.
  const hour = (values['hour'] ?? 0) % 24;
  return {
    year: values['year'] ?? 0,
    month: values['month'] ?? 1,
    day: values['day'] ?? 1,
    hour,
    minute: values['minute'] ?? 0,
    second: values['second'] ?? 0,
  };
}

function pad(value: number, width: number): string {
  return String(value).padStart(width, '0');
}

function formatLocalDate(year: number, month: number, day: number): LocalDate {
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}

/** Parses a LocalDate to the UTC epoch ms of that date's 00:00 "as if UTC". Throws on bad input. */
function localDateToUtcMs(date: LocalDate): number {
  const match = LOCAL_DATE_PATTERN.exec(date);
  if (!match || !isValidLocalDate(date)) {
    throw new RangeError(`Invalid LocalDate: ${date}`);
  }
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function utcMsToLocalDate(ms: number): LocalDate {
  const d = new Date(ms);
  return formatLocalDate(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate());
}

function truncateToSecond(ms: number): number {
  return Math.floor(ms / MS_PER_SECOND) * MS_PER_SECOND;
}

export function isValidTimeZone(tz: string): boolean {
  if (typeof tz !== 'string' || tz.trim() === '') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export function isValidLocalDate(d: string): boolean {
  if (typeof d !== 'string') return false;
  const match = LOCAL_DATE_PATTERN.exec(d);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const probe = new Date(Date.UTC(year, month - 1, day));
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
}

export function instantToLocal(instant: Date, tz: IanaZone): { date: LocalDate; second: number } {
  const wall = wallClockAt(instant, tz);
  return {
    date: formatLocalDate(wall.year, wall.month, wall.day),
    second: wall.hour * SECONDS_PER_HOUR + wall.minute * SECONDS_PER_MINUTE + wall.second,
  };
}

export function todayIn(tz: IanaZone, now: Date = new Date()): LocalDate {
  return instantToLocal(now, tz).date;
}

export function zoneOffsetMinutes(tz: IanaZone, instant: Date): number {
  const wall = wallClockAt(instant, tz);
  const wallAsUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
  return Math.round((wallAsUtc - truncateToSecond(instant.getTime())) / MS_PER_MINUTE);
}

/**
 * Converts a local wall-clock time to an instant.
 * - Gap (local time does not exist, DST spring-forward): interpreted with the
 *   PRE-transition offset, which lands after the gap.
 * - Overlap (local time occurs twice, DST fall-back): returns the EARLIER instant.
 */
export function localToInstant(date: LocalDate, second: number, tz: IanaZone): Date {
  if (!Number.isInteger(second) || second < 0 || second >= SECONDS_PER_DAY) {
    throw new RangeError(`second must be an integer in 0..86399, got ${second}`);
  }
  const wallMs = localDateToUtcMs(date) + second * MS_PER_SECOND;
  const offsetBefore = zoneOffsetMinutes(tz, new Date(wallMs - OFFSET_PROBE_MS));
  const offsetAfter = zoneOffsetMinutes(tz, new Date(wallMs + OFFSET_PROBE_MS));

  const candidates = [wallMs - offsetBefore * MS_PER_MINUTE, wallMs - offsetAfter * MS_PER_MINUTE];
  const valid = candidates.filter((ms) => {
    const local = instantToLocal(new Date(ms), tz);
    return local.date === date && local.second === second;
  });

  if (valid.length > 0) return new Date(Math.min(...valid));
  // Gap: no instant shows this wall time; use the pre-transition offset.
  return new Date(wallMs - offsetBefore * MS_PER_MINUTE);
}

export function addDays(date: LocalDate, n: number): LocalDate {
  return utcMsToLocalDate(localDateToUtcMs(date) + n * MS_PER_DAY);
}

/** a − b in whole days. */
export function diffDays(a: LocalDate, b: LocalDate): number {
  return Math.round((localDateToUtcMs(a) - localDateToUtcMs(b)) / MS_PER_DAY);
}

export function isoWeekday(date: LocalDate): 1 | 2 | 3 | 4 | 5 | 6 | 7 {
  const jsDay = new Date(localDateToUtcMs(date)).getUTCDay();
  return (jsDay === 0 ? 7 : jsDay) as 1 | 2 | 3 | 4 | 5 | 6 | 7;
}

/** Monday of the ISO week containing `date`. */
export function weekStart(date: LocalDate): LocalDate {
  return addDays(date, 1 - isoWeekday(date));
}

export function weekRange(date: LocalDate): { start: LocalDate; end: LocalDate } {
  const start = weekStart(date);
  return { start, end: addDays(start, 6) };
}

/** First and last day of the calendar month containing `date` (leap Februaries included). */
export function monthRange(date: LocalDate): { start: LocalDate; end: LocalDate } {
  const ms = localDateToUtcMs(date);
  const d = new Date(ms);
  const year = d.getUTCFullYear();
  const month = d.getUTCMonth();
  const start = formatLocalDate(year, month + 1, 1);
  // Date.UTC normalises month 12 to January of the next year.
  const nextStart = utcMsToLocalDate(Date.UTC(year, month + 1, 1));
  return { start, end: addDays(nextStart, -1) };
}

/** Inclusive list of dates; [] when end < start. */
export function datesInRange(start: LocalDate, end: LocalDate): LocalDate[] {
  const count = diffDays(end, start);
  const result: LocalDate[] = [];
  for (let i = 0; i <= count; i += 1) result.push(addDays(start, i));
  return result;
}

/** Dates in [from, to] whose ISO weekday (1 = Mon … 7 = Sun) is in workweek, ascending (sprint 5). */
export function workingDates(from: LocalDate, to: LocalDate, workweek: readonly number[]): LocalDate[] {
  const days = new Set(workweek);
  return datesInRange(from, to).filter((d) => days.has(isoWeekday(d)));
}

/**
 * Splits the real-time span [start, end) into one segment per local calendar day.
 * Instants are truncated to whole seconds first. durationSeconds is real elapsed time,
 * so DST days produce segments shorter or longer than their wall-clock span.
 * Segment boundaries are localToInstant(nextDate, 0): when a local midnight does not
 * exist, the next segment starts at the first valid local second (e.g. 3600).
 */
export function splitAtMidnights(start: Date, end: Date, tz: IanaZone): DaySegment[] {
  if (end.getTime() < start.getTime()) {
    throw new RangeError('splitAtMidnights: end is before start');
  }
  const endMs = truncateToSecond(end.getTime());
  let cursorMs = truncateToSecond(start.getTime());
  const segments: DaySegment[] = [];

  while (cursorMs < endMs) {
    const local = instantToLocal(new Date(cursorMs), tz);
    const nextMidnightMs = localToInstant(addDays(local.date, 1), 0, tz).getTime();
    // Progress guard: a boundary at or before the cursor would loop forever.
    const boundaryMs = nextMidnightMs > cursorMs ? nextMidnightMs : cursorMs + MS_PER_DAY;
    const segmentEndMs = Math.min(boundaryMs, endMs);
    segments.push({
      date: local.date,
      startSecond: local.second,
      durationSeconds: (segmentEndMs - cursorMs) / MS_PER_SECOND,
    });
    cursorMs = segmentEndMs;
  }
  return segments;
}

/**
 * Splits a local span (date + startSecond + real durationSeconds) at every local midnight.
 * start = localToInstant(date, startSecond, tz); end = start + durationSeconds; then splitAtMidnights,
 * so manual overnight entries and timer segments share one algorithm (DST and skipped midnights included).
 * The 24h limit is the caller's (validated before the split, RD section 6 A5 note).
 */
export function splitLocalSpan(date: LocalDate, startSecond: number, durationSeconds: number, tz: IanaZone): DaySegment[] {
  const start = localToInstant(date, startSecond, tz);
  const end = new Date(start.getTime() + durationSeconds * MS_PER_SECOND);
  return splitAtMidnights(start, end, tz);
}

/** Local date + second at which the span ends (DST-aware). End exactly at midnight gives { next day, 0 }. */
export function localSpanEnd(
  date: LocalDate,
  startSecond: number,
  durationSeconds: number,
  tz: IanaZone,
): { date: LocalDate; second: number } {
  const start = localToInstant(date, startSecond, tz);
  return instantToLocal(new Date(start.getTime() + durationSeconds * MS_PER_SECOND), tz);
}

/**
 * Real seconds from (date, startSecond) to the End clock time typed in a form.
 * endSecond > startSecond: same date; endSecond <= startSecond: next date (overnight), so an End
 * equal to Start reads as 24h on a normal day. Floored to whole seconds; the caller validates the result.
 */
export function durationToLocalEnd(date: LocalDate, startSecond: number, endSecond: number, tz: IanaZone): number {
  const endDate = endSecond > startSecond ? date : addDays(date, 1);
  const startMs = localToInstant(date, startSecond, tz).getTime();
  const endMs = localToInstant(endDate, endSecond, tz).getTime();
  return Math.floor((endMs - startMs) / MS_PER_SECOND);
}
